import { useCallback, useMemo, useRef, useState } from 'react';

import {
  applySnapToValue,
  areArraysEqual,
  areValuesEqual,
  buildMarks,
  constrainRangeValue,
  createChangeEvent,
  getSnapThreshold,
  getTrackMetrics,
  normalizeMinDistance,
  normalizeShiftStep,
  normalizeSliderValue,
  normalizeSnapToMarks,
  normalizeStep,
  normalizeVisibleInputValue,
  resolveKeyboardSnapValue,
  resolveTrackMode,
} from '../utils';

import useSliderBase from './use-slider-base';

import type * as React from 'react';

import type {
  EmptyProps,
  GetHiddenInputProps,
  SliderValue,
  UseSliderHiddenInputProps,
  UseSliderParameters,
  UseSliderReturnValue,
} from '../types';

const INCOMPLETE_NUMBER_INPUT_VALUES = new Set(['', '+', '+.', '-', '-.', '.']);

const formatVisibleInputValue = (value: number): string => `${value}`;

/**
 * Primary hook for the Slider component. Wraps `useSliderBase` and adds:
 * - **Snap-to-marks** — magnetically snaps thumb values to nearby marks during
 *   pointer drag, keyboard navigation, and number input entry.
 * - **Track metrics** — recomputes `trackOffset`/`trackLeap` for `center` track
 *   mode and snap-adjusted values.
 * - **Number input handling** — keeps visible number inputs in a transient text
 *   state while the user types, then commits normalized values on blur or Enter.
 * - **Read-only enforcement** — blocks hidden input changes in read-only mode
 *   by intercepting `onChangeCapture` and `onKeyDownCapture`.
 *
 * @param parameters - Slider configuration (extends `UseSliderBaseParameters`).
 * @returns State, props getters, and handlers needed by the `Slider` component.
 */
export const useSlider = (parameters: UseSliderParameters): UseSliderReturnValue => {
  const {
    defaultValue,
    marks: marksProp = false,
    max = 100,
    min = 0,
    minDistance = 0,
    name,
    onChange,
    onChangeCommitted,
    readOnly = false,
    shiftStep,
    snapToMarks = false,
    step = 1,
    track = 'normal',
    value,
    withNumberInput = false,
    ...baseParameters
  } = parameters;

  const resolvedMinDistance = useMemo(() => normalizeMinDistance(minDistance), [minDistance]);
  const resolvedShiftStep = useMemo(() => normalizeShiftStep(shiftStep ?? 10), [shiftStep]);
  const resolvedStep = useMemo(() => normalizeStep(step, marksProp), [marksProp, step]);
  const resolvedSnapToMarks = useMemo(() => normalizeSnapToMarks(snapToMarks), [snapToMarks]);
  const resolvedMarks = useMemo(
    () => buildMarks(marksProp, min, max, resolvedStep),
    [marksProp, max, min, resolvedStep],
  );
  const normalizedDefaultValue = useMemo(() => {
    return normalizeSliderValue(defaultValue, {
      marks: resolvedMarks,
      max,
      min,
      minDistance: resolvedMinDistance,
      step: resolvedStep,
    });
  }, [defaultValue, max, min, resolvedMarks, resolvedMinDistance, resolvedStep]);
  const normalizedValue = useMemo(() => {
    return normalizeSliderValue(value, {
      marks: resolvedMarks,
      max,
      min,
      minDistance: resolvedMinDistance,
      step: resolvedStep,
    });
  }, [max, min, resolvedMarks, resolvedMinDistance, resolvedStep, value]);
  const marksArray = Array.isArray(marksProp) ? resolvedMarks : undefined;
  // Snapping requires explicit mark positions and a step grid (step !== null)
  const snapEnabled = !!resolvedSnapToMarks && (marksArray?.length ?? 0) > 0 && resolvedStep !== null;

  const snapThreshold = useMemo(() => {
    return getSnapThreshold(snapEnabled, resolvedSnapToMarks, min, max);
  }, [max, min, resolvedSnapToMarks, snapEnabled]);

  const emitChange = useCallback(
    (event: Event, newValue: SliderValue, activeThumb: number) => {
      onChange?.(event, newValue, activeThumb);
    },
    [onChange],
  );

  const emitChangeCommitted = useCallback(
    (event: Event | React.SyntheticEvent, newValue: SliderValue) => {
      onChangeCommitted?.(event, newValue);
    },
    [onChangeCommitted],
  );

  const handleChange = useCallback(
    (event: Event, newValue: SliderValue, activeThumb: number) => {
      if (!snapEnabled || !marksArray) {
        emitChange(event, newValue, activeThumb);
        return;
      }

      emitChange(event, applySnapToValue(newValue, marksArray, snapThreshold), activeThumb);
    },
    [emitChange, marksArray, snapEnabled, snapThreshold],
  );

  const handleChangeCommitted = useCallback(
    (event: Event | React.SyntheticEvent, newValue: SliderValue) => {
      if (!snapEnabled || !marksArray) {
        emitChangeCommitted(event, newValue);
        return;
      }

      emitChangeCommitted(event, applySnapToValue(newValue, marksArray, snapThreshold));
    },
    [emitChangeCommitted, marksArray, snapEnabled, snapThreshold],
  );

  const resolveKeyboardValue = useCallback(
    (currentValue: number, nextValue: number) => {
      if (!snapEnabled || !marksArray) {
        return nextValue;
      }

      return resolveKeyboardSnapValue({
        currentValue,
        marks: marksArray,
        max,
        min,
        nextValue,
        snapThreshold,
      });
    },
    [marksArray, max, min, snapEnabled, snapThreshold],
  );

  const baseSlider = useSliderBase({
    ...baseParameters,
    defaultValue: normalizedDefaultValue,
    marks: marksProp,
    max,
    min,
    minDistance: resolvedMinDistance,
    name,
    onChange: readOnly ? undefined : handleChange,
    onChangeCommitted: readOnly ? undefined : handleChangeCommitted,
    readOnly,
    resolveKeyboardValue,
    shiftStep: resolvedShiftStep,
    step: resolvedStep,
    value: normalizedValue,
  });

  const {
    getHiddenInputProps: getBaseHiddenInputProps,
    marks,
    range,
    setValueState,
    trackLeap: baseTrackLeap,
    trackOffset: baseTrackOffset,
    values,
  } = baseSlider;

  const resolvedTrack = useMemo(() => resolveTrackMode(track, range), [range, track]);
  const activeInputIndexRef = useRef<null | number>(null);
  const pendingCommittedValuesRef = useRef<null | number[]>(null);
  const [draftInputValues, setDraftInputValues] = useState<string[]>([]);

  // When snap is enabled, the visual thumb positions may differ from the internal
  // values. `renderValues` are the snapped positions used for rendering, while
  // `values` remain the raw positions used for state management and callbacks.
  const renderValues = useMemo(() => {
    if (!snapEnabled || !marksArray) {
      return values;
    }

    return values.map((sliderValue) => applySnapToValue(sliderValue, marksArray, snapThreshold) as number);
  }, [marksArray, snapEnabled, snapThreshold, values]);

  const trackMetrics = useMemo(() => {
    return getTrackMetrics(renderValues, range, min, max, resolvedTrack);
  }, [max, min, range, renderValues, resolvedTrack]);

  const syncedInputValues = useMemo(() => {
    if (!withNumberInput) {
      return [];
    }

    return values.map((sliderValue) => formatVisibleInputValue(sliderValue));
  }, [withNumberInput, values]);

  if (!withNumberInput) {
    activeInputIndexRef.current = null;
    pendingCommittedValuesRef.current = null;
  } else if (pendingCommittedValuesRef.current != null && areArraysEqual(values, pendingCommittedValuesRef.current)) {
    pendingCommittedValuesRef.current = null;
  }

  const inputValues = useMemo(() => {
    if (!withNumberInput) {
      return [];
    }

    const activeInputIndex = activeInputIndexRef.current;
    const hasPendingCommit = pendingCommittedValuesRef.current != null;

    if (hasPendingCommit && draftInputValues.length === syncedInputValues.length) {
      return draftInputValues.map((draftValue, index) => draftValue ?? syncedInputValues[index] ?? '');
    }

    if (activeInputIndex == null || draftInputValues[activeInputIndex] == null) {
      return syncedInputValues;
    }

    const nextInputValues = [...syncedInputValues];
    nextInputValues[activeInputIndex] = draftInputValues[activeInputIndex] ?? nextInputValues[activeInputIndex] ?? '';
    return nextInputValues;
  }, [draftInputValues, withNumberInput, syncedInputValues]);

  const normalizeInputValue = useCallback(
    (inputValue: number) => {
      return normalizeVisibleInputValue({
        marks,
        max,
        min,
        snapThreshold,
        snapToMarks: snapEnabled,
        step: resolvedStep,
        value: inputValue,
      });
    },
    [marks, max, min, resolvedStep, snapEnabled, snapThreshold],
  );

  const resolveNumberInputValue = useCallback(
    (inputValue: number, thumbIndex: number): { activeIndex: number; value: SliderValue } => {
      const normalizedValue = normalizeInputValue(inputValue);

      if (!range) {
        return { activeIndex: thumbIndex, value: normalizedValue };
      }

      const constrainedRange = constrainRangeValue(values, normalizedValue, thumbIndex, {
        disableSwap: baseParameters.disableSwap,
        max,
        min,
        minDistance: resolvedMinDistance,
      });

      return {
        activeIndex: constrainedRange.activeIndex,
        value: [constrainedRange.value[0] ?? min, constrainedRange.value[1] ?? min],
      };
    },
    [baseParameters.disableSwap, max, min, normalizeInputValue, range, resolvedMinDistance, values],
  );

  const resetInputValue = useCallback(
    (thumbIndex: number) => {
      activeInputIndexRef.current = null;
      pendingCommittedValuesRef.current = null;

      setDraftInputValues((currentValues) => {
        const nextValues = [...syncedInputValues];

        if (nextValues[thumbIndex] == null) {
          return currentValues;
        }

        nextValues[thumbIndex] = syncedInputValues[thumbIndex] ?? '';
        return areArraysEqual(currentValues, nextValues) ? currentValues : nextValues;
      });
    },
    [syncedInputValues],
  );

  const commitNumberInputValue = useCallback(
    (event: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, thumbIndex: number) => {
      if (readOnly) {
        return;
      }

      const rawValue = event.currentTarget.value.trim();

      if (INCOMPLETE_NUMBER_INPUT_VALUES.has(rawValue)) {
        resetInputValue(thumbIndex);
        return;
      }

      const nextNumber = Number(rawValue);

      if (Number.isNaN(nextNumber)) {
        resetInputValue(thumbIndex);
        return;
      }

      const previousValue: SliderValue = range ? [values[0] ?? min, values[1] ?? min] : (values[0] ?? min);
      const { activeIndex, value: newValue } = resolveNumberInputValue(nextNumber, thumbIndex);
      const nextCommittedValues = Array.isArray(newValue) ? [...newValue] : [newValue];
      const nextInputValues = nextCommittedValues.map((sliderValue) => formatVisibleInputValue(sliderValue));

      activeInputIndexRef.current = null;
      pendingCommittedValuesRef.current = nextCommittedValues;
      setDraftInputValues(nextInputValues);
      setValueState(newValue);

      if (!areValuesEqual(newValue, previousValue)) {
        onChange?.(createChangeEvent(event, newValue, name), newValue, activeIndex);
        onChangeCommitted?.(event, newValue);
      }
    },
    [
      min,
      name,
      onChange,
      onChangeCommitted,
      range,
      readOnly,
      resetInputValue,
      resolveNumberInputValue,
      setValueState,
      values,
    ],
  );

  const handleNumberInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, thumbIndex: number) => {
      if (readOnly) {
        return;
      }

      activeInputIndexRef.current = thumbIndex;
      pendingCommittedValuesRef.current = null;

      setDraftInputValues((currentValues) => {
        const nextValues = currentValues.length > 0 ? [...currentValues] : [...syncedInputValues];

        nextValues[thumbIndex] = event.target.value;
        return nextValues;
      });
    },
    [readOnly, syncedInputValues],
  );

  const handleNumberInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>, thumbIndex: number) => {
      if (readOnly) {
        return;
      }

      if (activeInputIndexRef.current !== thumbIndex) {
        return;
      }

      commitNumberInputValue(event, thumbIndex);
    },
    [commitNumberInputValue, readOnly],
  );

  const handleNumberInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, thumbIndex: number) => {
      if (event.key !== 'Enter') {
        return;
      }

      commitNumberInputValue(event, thumbIndex);
    },
    [commitNumberInputValue],
  );

  const preventReadOnlyInputChange = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const preventReadOnlyInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const getHiddenInputProps: GetHiddenInputProps = <ExternalProps extends Record<string, unknown> = EmptyProps>(
    externalProps: ExternalProps = {} as ExternalProps,
  ): UseSliderHiddenInputProps<ExternalProps> => {
    return getBaseHiddenInputProps({
      ...externalProps,
      ...(readOnly
        ? {
            onChangeCapture: preventReadOnlyInputChange,
            onKeyDownCapture: preventReadOnlyInputKeyDown,
            tabIndex: -1,
          }
        : {}),
    } as ExternalProps);
  };

  return {
    ...baseSlider,
    customMarks: marksArray,
    getHiddenInputProps,
    handleNumberInputBlur,
    handleNumberInputChange,
    handleNumberInputKeyDown,
    inputValues,
    renderValues,
    resolvedStep,
    resolvedTrack,
    // Use recomputed track metrics when snap or center-track mode is active,
    // because the base metrics use raw (unsnapped) values.
    trackLeap: snapEnabled || resolvedTrack === 'center' ? trackMetrics.leap : baseTrackLeap,
    trackOffset: snapEnabled || resolvedTrack === 'center' ? trackMetrics.offset : baseTrackOffset,
  };
};

export default useSlider;
